import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import './Parts.css';

const apiBaseURL = (process.env.REACT_APP_API_BASE_URL || 'https://api.vehiclesolution.co').replace(/\/+$/, '');

function Parts({ onLogout }) {
  const navigate = useNavigate();
  const [parts, setParts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 10, total: 0, totalPages: 1 });

  const fetchParts = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiBaseURL}/api/parts?page=${page}&pageSize=${pageSize}&search=${encodeURIComponent(searchTerm.trim())}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setParts(data.parts || []);
        setPagination(data.pagination || { page, pageSize, total: 0, totalPages: 1 });
      } else {
        setError(data.message || 'Failed to load parts.');
      }
    } catch (fetchError) {
      console.error('Error fetching parts:', fetchError);
      setError('Failed to load parts.');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchTerm]);

  useEffect(() => {
    fetchParts();
  }, [fetchParts]);

  return (
    <div className="home-container parts-page-shell">
      <Sidebar onLogout={onLogout} />
      <div className="main-section parts-main-section">
        <div className="parts-container">
          <header className="parts-header">
            <button className="app-back-button" onClick={() => navigate('/inventory')}>
              Back
            </button>
            <h1>Parts</h1>
            <div className="header-buttons">
              <button className="btn-add" onClick={() => navigate('/add-part')}>+ Add Part</button>
              <button className="btn-alt-part" onClick={() => navigate('/add-alternative-part')}>Alternative Part#</button>
            </div>
          </header>

          <div className="parts-content">
            <div className="search-box">
              <div className="parts-toolbar">
                <input
                  type="text"
                  placeholder="Search parts..."
                  value={searchTerm}
                  onChange={(e) => {
                    setPage(1);
                    setSearchTerm(e.target.value);
                  }}
                />
                <div className="parts-toolbar-actions">
                  <select
                    className="parts-page-size"
                    value={pageSize}
                    onChange={(e) => {
                      setPage(1);
                      setPageSize(parseInt(e.target.value, 10));
                    }}
                  >
                    <option value={10}>10 rows</option>
                    <option value={20}>20 rows</option>
                    <option value={50}>50 rows</option>
                  </select>
                  <button className="parts-refresh" onClick={fetchParts}>Refresh</button>
                </div>
              </div>
            </div>

            <div className="parts-results">
              {loading ? (
                <div className="loading-state">
                  <p>Loading parts...</p>
                </div>
              ) : error ? (
                <div className="loading-state">
                  <p>{error}</p>
                </div>
              ) : parts.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">{'\u{1F4E6}'}</div>
                  <h2>No Parts Found</h2>
                  <p>Start adding parts to your inventory</p>
                </div>
              ) : (
                <div className="parts-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Part ID</th>
                        <th>Part Name</th>
                        <th>Part Number</th>
                        <th>Description</th>
                        <th>Vendor</th>
                        <th>Brand</th>
                        <th>Location</th>
                        <th>Part Type</th>
                        <th>Stock</th>
                        <th>Unit Value</th>
                        <th>Unit</th>
                        <th>Limit</th>
                        <th>Opening Date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parts.map((part, index) => (
                        <tr key={part.PartId || index}>
                          <td>{part.PartId}</td>
                          <td>{part.parts_name}</td>
                          <td>{part.Part_no}</td>
                          <td>{part.part_description}</td>
                          <td>{part.vendor_name || '-'}</td>
                          <td>{part.Brand}</td>
                          <td>{part.Location}</td>
                          <td>{part.Part_type}</td>
                          <td>{part.Qty ?? part.opening_qty ?? 0}</td>
                          <td>Tk.{Number(part.opening_value || 0).toFixed(2)}</td>
                          <td>{part.unit}</td>
                          <td>{part.Limit}</td>
                          <td>{part.opening_date ? new Date(part.opening_date).toLocaleDateString() : '-'}</td>
                          <td>
                            <button className="btn-edit">Edit</button>
                            <button className="btn-delete">Delete</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {!loading && !error && (
                <div className="parts-pagination">
                  <div className="parts-page-info">
                    Showing page {pagination.page} of {pagination.totalPages} | Total rows: {pagination.total}
                  </div>
                  <div className="parts-page-buttons">
                    <button
                      className="parts-refresh"
                      onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                      disabled={pagination.page <= 1}
                    >
                      Previous
                    </button>
                    <button
                      className="parts-refresh"
                      onClick={() => setPage((prev) => Math.min(prev + 1, pagination.totalPages))}
                      disabled={pagination.page >= pagination.totalPages}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Parts;
